"use client";

import React, { useEffect, useRef, useState } from "react";

import {
  OTPContextProvider,
  OTPContextWrapper,
  useOTPContext,
} from "@/components/sections/CommonModals/context";
import ProgramDetailsOpenRfi from "./ProgramDetailsOpenRfi";
import { StorageService } from "@/helpers/storage.service";

import styles from "./NewBannerFormLp.module.scss";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue, updateStateSession } from "@/context/utils";
import ApplicationFormRedirectLoader from "@/components/molecules/ApplicationFormRedirectLoader/ApplicationFormRedirectLoader";
import { landingPagesApplicationJourneyFlow } from "@/components/molecules/Forms/LandingPageConnectModalForm/utils";
import { usePathname } from "next/navigation";

interface BannerImageTempleteType {
  ShortDescLabel1?: string;
  ShortDescLabel2?: string;
  ShortDescLabel3?: string;
  ShortDescSubLabel1?: string;
  ShortDescSubLabel2?: string;
  ShortDescSubLabel3?: string;
  SeatFilled?: string;
}

interface NewbannerFormTempleteProps {
  isMobile?: boolean;
  isFaq?: boolean;
  associatedPrograms?: any;
  ProgramId?: any;
  FaqHeading?: string;
  RFIHeading?: string;
  sectionName?: string;
  BannerImageTemplete?: BannerImageTempleteType;
}

interface LpBannerFormBodyProps {
  isFaq: boolean;
  FaqHeading: string;
  RFIHeading: string;
  isMobile: boolean;
  ProgramId: any;
  associatedPrograms: any;
  BannerImageTemplete: BannerImageTempleteType;
  setApplicationRedirectLoaderDisabled: (value: boolean) => void;
  setRedirectEmail: (email: string) => void;
  setLoadingCount: React.Dispatch<React.SetStateAction<number>>;
  counterInterval: React.MutableRefObject<ReturnType<typeof setInterval> | null>;
  isExistingUserRef: React.MutableRefObject<boolean>;
}

/** Reads OTP context only here — avoids Consumer re-rendering the whole template on leadResponse updates (INP). */
function LpBannerFormBody({
  isFaq,
  FaqHeading,
  RFIHeading,
  isMobile,
  ProgramId,
  associatedPrograms,
  BannerImageTemplete,
  setApplicationRedirectLoaderDisabled,
  setRedirectEmail,
  setLoadingCount,
  counterInterval,
  isExistingUserRef,
}: LpBannerFormBodyProps) {
  const { leadResponse, isOTPSent } = useOTPContext();

  useEffect(() => {
    isExistingUserRef.current = leadResponse?.ID == "0";
  }, [isExistingUserRef, leadResponse?.ID]);

  return (
    <>
      {/* Keep RFI title until OTP mounts — leadResponse can arrive earlier than
          isOTPSent and used to hide the heading first (flicker/blank). */}
      {!isOTPSent && (
        <div className={`${isFaq ? styles.headingFaq : styles.heading}`}>
          {isFaq ? FaqHeading : RFIHeading}
        </div>
      )}
      <OTPContextWrapper isNewProgramTemp isLambdaAPI>
        <ProgramDetailsOpenRfi
          isFaq={isFaq}
          ProgramId={ProgramId}
          associatedPrograms={associatedPrograms}
          isMobile={isMobile}
          setApplicationRedirectLoaderDisabled={setApplicationRedirectLoaderDisabled}
          BannerImageTemplete={BannerImageTemplete}
          setLoadingCount={setLoadingCount}
          counterInterval={counterInterval}
          setRedirectEmail={setRedirectEmail}
        />
      </OTPContextWrapper>
    </>
  );
}

export default function NewbannerFormTempleteLp({
  isMobile = false,
  isFaq = false,
  associatedPrograms,
  ProgramId,
  FaqHeading = "",
  RFIHeading = "",
  sectionName = "hero_banner",
  BannerImageTemplete = {},
}: NewbannerFormTempleteProps) {
  const [isApplicationRedirectLoaderDisabled, setApplicationRedirectLoaderDisabled] =
    useState(true);
  const [redirectEmail, setRedirectEmail] = useState("");
  const ref = useRef(null);
  const isClickedOnce = useRef(false);
  const [loadingCount, setLoadingCount] = useState(3);
  const path = usePathname();
  const counterInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const isExistingUserRef = useRef(false);

  const startLoader = () => {
    counterInterval.current = setInterval(() => {
      setLoadingCount((count) => {
        if (count < 1) {
          if (counterInterval.current) {
            clearInterval(counterInterval.current);
          }

          if (
            isExistingUserRef.current &&
            landingPagesApplicationJourneyFlow.includes(path)
          ) {
            window.location.href =
              sessionStorage.getItem("redirectionUrl") || "/thankyou";
          } else {
            window.location.href = `/thankyou`;
          }

          StorageService.set("emailEvent", redirectEmail);

          setTimeout(() => {
            setApplicationRedirectLoaderDisabled(true);
          }, 1500);

          return 0;
        }
        return count - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    if (!isApplicationRedirectLoaderDisabled) {
      startLoader();
    }
  }, [isApplicationRedirectLoaderDisabled]);

  const gtEventHandler = (e: React.MouseEvent) => {
    // Submit clicks must not fire lead_initiate (invalid submits especially).
    const target = e.target as HTMLElement | null;
    if (target?.closest?.('button#submit-button, button[type="submit"]')) {
      return;
    }

    if (!isClickedOnce.current) {
      pushToDataLayer({
        event: "lead_initiate",
        cta_text: "Open RFI",
        section_name: sectionName,
        page_type: getGlobalStateValue("pageType"),
        page_url: window.location.href,
      });

      updateStateSession({
        parentSection: sectionName,
        leadStartingCta: "Open RFI",
      });

      isClickedOnce.current = true;
    }
  };

  useEffect(() => {
    return () => {
      if (counterInterval.current) {
        clearInterval(counterInterval.current);
      }
    };
  }, []);

  return (
    <div
      onClick={gtEventHandler}
      id="#leadform"
      ref={ref}
      className={`${styles.root} ${isFaq && styles.faqroot} self-start`}
    >
      {!isApplicationRedirectLoaderDisabled ? (
        <ApplicationFormRedirectLoader redirectTime={loadingCount} text="" />
      ) : (
        <OTPContextProvider isLambdaAPI>
          <LpBannerFormBody
            isFaq={isFaq}
            FaqHeading={FaqHeading}
            RFIHeading={RFIHeading}
            isMobile={isMobile}
            ProgramId={ProgramId}
            associatedPrograms={associatedPrograms}
            BannerImageTemplete={BannerImageTemplete}
            setApplicationRedirectLoaderDisabled={setApplicationRedirectLoaderDisabled}
            setRedirectEmail={setRedirectEmail}
            setLoadingCount={setLoadingCount}
            counterInterval={counterInterval}
            isExistingUserRef={isExistingUserRef}
          />
        </OTPContextProvider>
      )}
    </div>
  );
}
